import { useIntl } from '@doxyme/locale';
import type { GetRulesProps, GetRulesReturn, UseFormRules } from './types';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import type { TimeInputValue } from '../timeInput';
import { type DateInputValue } from '../dateInput';
import { isValidDate } from '../../../../utils';

export const useFormRules = (): UseFormRules => {
  const { formatMessage } = useIntl();

  const getRules = ({
    values: { minLength, required, requiredTrimSpaces, email },
    friendlyName,
  }: GetRulesProps): GetRulesReturn => {
    const getIntlId = (id: string, value: boolean | number): string =>
      formatMessage(
        { id },
        {
          friendlyName:
            friendlyName === undefined
              ? formatMessage({
                  id: 'form.validation.defaultFriendlyName',
                })
              : friendlyName,
          value,
        },
      );

    const rules: GetRulesReturn = {};

    if (required !== undefined) {
      rules.required = {
        value: required,
        message: getIntlId('form.validation.required', required),
      };
    }

    if (minLength !== undefined) {
      rules.minLength = {
        value: minLength,
        message: getIntlId('form.validation.minLength', minLength),
      };
    }

    if (requiredTrimSpaces === true) {
      rules.required = {
        value: true,
        message: getIntlId('form.validation.required', true),
      };
      rules.pattern = {
        value: /\S/u,
        message: getIntlId('form.validation.required', true),
      };
    }

    if (email === true) {
      /**
       * Same as backend email validation, https://github.com/ajv-validator/ajv-formats/blob/master/src/formats.ts#L65
       */
      const EMAIL_REGEX =
        /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/iu;

      rules.pattern = {
        value: EMAIL_REGEX,
        message: getIntlId('form.validation.email', true),
      };
    }

    return rules;
  };

  /**
   * Commonly used validation functions that can be passed to `react-hook-form`'s `validate` function
   * to return either true (validation passed) or an i18n-translated error message.
   */
  const validateDate = (value: DateInputValue): boolean | string => {
    if (value === null) {
      return true;
    }

    return (
      isValidDate(value) ||
      formatMessage({ id: 'form.validation.incorrectDateFormat' })
    );
  };

  const validateTime = (value: TimeInputValue): boolean | string => {
    if (value === null) {
      return true;
    }

    return (
      isValidDate(value) ||
      formatMessage({ id: 'form.validation.incorrectTimeFormat' })
    );
  };

  const validatePhone = (value: string | undefined): boolean | string => {
    const isValid = isValidPhoneNumber(value ?? '');

    return (
      isValid || formatMessage({ id: 'form.validation.incorrectPhoneFormat' })
    );
  };

  const validateUnsafePattern = (
    value: string | undefined,
    friendlyName: string,
  ): boolean | string => {
    const isMalicious =
      // eslint-disable-next-line no-useless-escape
      /(<|>|[{]|[}]|((https?:)?\/\/|www\.)[^\/\s]+|\{\{.*\}\})/u.test(
        value ?? '',
      );

    return (
      !isMalicious ||
      formatMessage(
        { id: 'form.validation.unsafePattern' },
        {
          friendlyName,
        },
      )
    );
  };

  return {
    getRules,
    validateDate,
    validateTime,
    validatePhone,
    validateUnsafePattern,
  };
};
